/**
 * "Map field" / "Edit mapping": the evaluator page's variable mappings, right
 * here. One row per variable — what it is, the field it reads (`Input › report`)
 * and that field's value in an example case — beside the example itself: pick
 * a row, click a field in the example to fill it, or choose one from the list
 * (grouped by where it comes from). A variable left as it was keeps the
 * evaluator's own path. By default only this experiment uses the change;
 * ticking "Also save as the evaluator's default" saves it as the next version.
 */

import { useState } from "react";
import { Link } from "react-router";
import { ExternalLink } from "lucide-react";
import { JsonView } from "@/components/ui/json-view";
import { MappingRows } from "@/features/evaluators/mapping-rows";
import { mappingPath } from "@/features/evaluators/sample-paths";
import type { Evaluator, PathInfo } from "@/features/evaluators/types";
import { useExampleCase, type ExampleScope } from "./example-case";
import type { EvaluatorFixes } from "./evaluator-overrides";

export interface FixProps {
  fixes: EvaluatorFixes;
  paths: PathInfo[];
  /** Where the example case comes from. */
  scope: ExampleScope;
}

export function EvaluatorFix({ e, fix }: { e: Evaluator; fix: FixProps }) {
  const override = fix.fixes.overrides[e.id] ?? {};
  const missing = new Set(e.missing ?? []);
  const [focused, setFocused] = useState<string | null>(
    e.variables.find((v) => missing.has(v.name))?.name ?? e.variables[0]?.name ?? null,
  );
  const example = useExampleCase(fix.scope);
  const value = Object.fromEntries(
    e.variables.map((v) => [
      v.name,
      override[v.name] ?? (missing.has(v.name) ? "" : (e.default_mapping[v.name] ?? "")),
    ]),
  );
  const pick = (raw: string) =>
    focused && fix.fixes.setPath(e.id, focused, example.live ? mappingPath(raw) : raw);
  const what = example.live ? "trace" : "item";

  return (
    <div
      className="mt-1.5 flex flex-col gap-3 rounded-lg bg-subtle p-3 text-xs"
      aria-label={`Map fields for ${e.name}`}
      role="group"
    >
      <div className="grid gap-3 md:grid-cols-2">
        <MappingRows
          rows={e.variables.map((v) => ({
            name: v.name,
            label: v.description || v.label,
            required: v.required,
          }))}
          value={value}
          paths={fix.paths}
          sample={example.view}
          focused={focused}
          onFocus={setFocused}
          onChange={(name, path) => fix.fixes.setPath(e.id, name, path)}
          hint={example.raw ? `Pick a row, then click a field in the example ${what} to fill it.` : ""}
        />
        <section aria-label={`Example ${what}`} className="flex min-w-0 flex-col gap-1.5">
          <span className="font-semibold">
            Example {what}
            {focused && example.raw && (
              <span className="font-normal text-faint"> · click a field for {`{{${focused}}}`}</span>
            )}
          </span>
          {example.raw ? (
            <JsonView value={example.raw} label={`Example ${what}`} onPick={pick} className="max-h-[260px]" />
          ) : (
            <span className="text-faint">
              {example.live ? "No recent traces match this dataset's source." : "No items to show."}
            </span>
          )}
        </section>
      </div>
      <label className="flex items-center gap-2 text-[13px]">
        <input
          type="checkbox"
          className="size-4 accent-ink"
          checked={fix.fixes.asDefault.has(e.id)}
          onChange={() => fix.fixes.toggleDefault(e.id)}
        />
        Also save as the evaluator's default (creates v{e.version + 1})
      </label>
      <span className="flex items-center justify-between gap-2 text-faint">
        {fix.fixes.asDefault.has(e.id)
          ? "Saved when the experiment is created."
          : "Only this experiment uses it. The evaluator is unchanged."}
        <Link
          className="link inline-flex items-center gap-1"
          to={`/evaluators/${e.id}`}
          target="_blank"
          rel="noreferrer"
        >
          Open evaluator
          <ExternalLink size={11} aria-hidden />
        </Link>
      </span>
    </div>
  );
}
